import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SiteNavbar from '../../components/SiteNavbar';
import SiteFooter from '../../components/SiteFooter';
import LineIcon from '../../components/LineIcon';
import { useTrialDays } from '../../utils/trialDays';
import Reveal from '../../components/Reveal';

const NAVY     = '#0D1B2A';
const NAVY_MID = '#132233';
const AMBER    = '#F5A623';
const WHITE    = '#FFFFFF';
const LIGHT_GRAY = '#F7F7F7';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

export default function EmployerLanding() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const trialDays = useTrialDays();
  const demoRef = useRef(null);
  const [form, setForm]       = useState({ name: '', company: '', email: '', phone: '' });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);

    const style = document.createElement('style');
    style.textContent = `
      * { box-sizing: border-box; margin: 0; padding: 0; }
      html { scroll-behavior: smooth; }
      body { background-color: ${NAVY}; }
      .emp-input::placeholder { color: rgba(255,255,255,0.38); }
      @media (max-width: 767px) {
        .emp-nav { padding: 0 1.25rem !important; }
        .emp-nav-links a { display: none; }
        .emp-nav-links { gap: 12px !important; }
        .emp-nav-post-job { display: none !important; }
        .emp-nav-book-demo { display: none !important; }
        .emp-headline { font-size: 48px !important; }
        .emp-hero-ctas { flex-direction: column; align-items: stretch; }
        .emp-hero-ctas > button, .emp-hero-ctas > a { width: 100%; justify-content: center; box-sizing: border-box; }
        .emp-why-grid { grid-template-columns: 1fr !important; }
        .emp-comp-table { min-width: 0 !important; table-layout: auto !important; }
        .emp-comp-competitor { display: none !important; }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  const scrollToDemo = () => demoRef.current?.scrollIntoView({ behavior: 'smooth' });

  const handleChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleSubmit = e => {
    e.preventDefault();
    if (form.name && form.company && form.email) setSubmitted(true);
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    fontSize: '1rem',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: '8px',
    outline: 'none',
    backgroundColor: 'rgba(255,255,255,0.07)',
    color: WHITE,
    fontFamily: FONT_BODY,
  };

  return (
    <div style={{ fontFamily: FONT_BODY, backgroundColor: NAVY, color: WHITE }}>

      {/* ── 1. NAV ── */}
      <SiteNavbar
        activePage="employers"
        signInPath="/login?type=employer"
        secondaryCta={{ label: t('employer.landing.postJobNav'), onClick: () => navigate('/employer/signup'), variant: 'outline' }}
        primaryCta={{ label: t('employer.landing.bookDemoNav'), onClick: scrollToDemo }}
      />

      {/* ── 2. HERO ── */}
      <section style={{
        padding: '120px 2rem 80px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: NAVY,
        borderBottom: `1.5px solid ${AMBER}`,
      }}>
        {/* Radial glow */}
        <div style={{
          position: 'absolute',
          top: '38%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '700px',
          height: '450px',
          background: 'radial-gradient(ellipse, rgba(245,166,35,0.13) 0%, transparent 68%)',
          pointerEvents: 'none',
        }} />

        <div style={{ position: 'relative', maxWidth: '860px', margin: '0 auto' }}>
          {/* Eyebrow */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: 'rgba(245,166,35,0.12)',
            border: '1px solid rgba(245,166,35,0.35)',
            borderRadius: '999px',
            padding: '7px 18px',
            fontSize: '0.8rem',
            fontWeight: 600,
            color: AMBER,
            marginBottom: '1.75rem',
            letterSpacing: '0.04em',
          }}>
            {t('employer.landing.eyebrow')}
          </div>

          {/* Headline */}
          <h1 className="emp-headline" style={{
            fontFamily: FONT_HEAD,
            fontSize: 'clamp(3.5rem, 10vw, 7rem)',
            lineHeight: 1.02,
            letterSpacing: '0.03em',
            color: WHITE,
            marginBottom: '1.25rem',
          }}>
            {t('employer.landing.headlinePre')}<br />
            <span style={{ color: AMBER }}>{t('employer.landing.headlineAmber')}</span>
          </h1>

          {/* Subtitle */}
          <p style={{
            fontSize: 'clamp(1rem, 2.2vw, 1.2rem)',
            color: 'rgba(255,255,255,0.7)',
            marginBottom: '2.75rem',
            lineHeight: 1.65,
            maxWidth: '640px',
            margin: '0 auto 2.75rem',
          }}>
            {t('employer.landing.subtitle')}
          </p>

          {/* CTAs */}
          <div className="emp-hero-ctas" style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginBottom: '2.75rem',
          }}>
            <button
              onClick={scrollToDemo}
              style={{
                backgroundColor: AMBER,
                color: NAVY,
                border: 'none',
                borderRadius: '8px',
                padding: '15px 30px',
                fontWeight: 700,
                fontSize: '1rem',
                cursor: 'pointer',
                fontFamily: FONT_BODY,
              }}
            >
              {t('employer.landing.bookFreeDemo')}
            </button>
            <a
              href="#how-it-works"
              style={{
                backgroundColor: 'transparent',
                color: WHITE,
                border: '2px solid rgba(255,255,255,0.28)',
                borderRadius: '8px',
                padding: '15px 30px',
                fontWeight: 600,
                fontSize: '1rem',
                fontFamily: FONT_BODY,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              {t('employer.landing.seeHowItWorks')}
            </a>
          </div>

          {/* Trust row */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '1.25rem 2rem',
            fontSize: '0.85rem',
            color: 'rgba(255,255,255,0.65)',
          }}>
            {[
              t('employer.landing.trust1'),
              t('employer.landing.trust2'),
              t('employer.landing.trust3'),
              t('employer.landing.trust4'),
            ].map(item => (
              <span key={item} style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                <LineIcon name="check" size={15} strokeWidth={2.6} color="#4ade80" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. HOW IT WORKS FOR EMPLOYERS ── */}
      <section id="how-it-works" style={{ backgroundColor: LIGHT_GRAY, padding: '7rem 2rem' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

          <Reveal>
            {/* Eyebrow */}
            <div style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: AMBER,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              marginBottom: '0.9rem',
            }}>
              {t('employer.landing.howItWorksEyebrow')}
            </div>

            <h2 style={{
              fontFamily: FONT_HEAD,
              fontSize: 'clamp(2.5rem, 5.5vw, 3.75rem)',
              color: NAVY,
              letterSpacing: '0.03em',
              marginBottom: '1.1rem',
              maxWidth: '740px',
              lineHeight: 1.1,
            }}>
              {t('employer.landing.howItWorksHeading')}
            </h2>

            <div style={{ width: '56px', height: '3px', backgroundColor: AMBER, marginBottom: '1.25rem' }} />

            <p style={{
              fontSize: '1.05rem',
              color: 'rgba(13,27,42,0.6)',
              marginBottom: '4.5rem',
              lineHeight: 1.65,
              maxWidth: '560px',
            }}>
              {t('employer.landing.howItWorksIntro')}
            </p>
          </Reveal>

          {/* Timeline */}
          <div style={{ position: 'relative', maxWidth: '760px' }}>
            {/* Dotted vertical line */}
            <div style={{
              position: 'absolute',
              left: '21px',
              top: '22px',
              bottom: '22px',
              borderLeft: '2px dashed rgba(245,166,35,0.38)',
              pointerEvents: 'none',
            }} />

            {[
              {
                num: '01', icon: 'building',
                title: t('employer.landing.step1Title'),
                desc: t('employer.landing.step1Desc'),
              },
              {
                num: '02', icon: 'clipboard',
                title: t('employer.landing.step2Title'),
                desc: t('employer.landing.step2Desc'),
              },
              {
                num: '03a', icon: 'bell',
                title: t('employer.landing.step3aTitle'),
                desc: t('employer.landing.step3aDesc'),
                orAfter: true,
              },
              {
                num: '03b', icon: 'search',
                title: t('employer.landing.step3bTitle'),
                desc: t('employer.landing.step3bDesc'),
              },
              {
                num: '04', icon: 'calendar',
                title: t('employer.landing.step4Title'),
                desc: t('employer.landing.step4Desc'),
              },
              {
                num: '05', icon: 'signature',
                title: t('employer.landing.step5Title'),
                desc: t('employer.landing.step5Desc'),
              },
              {
                num: '06', icon: 'message',
                title: t('employer.landing.step6Title'),
                desc: t('employer.landing.step6Desc'),
              },
            ].map((step, i, arr) => (
              <Reveal stagger key={step.num}>
                <div style={{
                  display: 'flex',
                  gap: '1.75rem',
                  alignItems: 'flex-start',
                  marginBottom: step.orAfter ? 0 : i < arr.length - 1 ? '2.75rem' : 0,
                }}>
                  {/* Icon box — sits on the dotted line */}
                  <div style={{
                    width: '44px',
                    height: '44px',
                    backgroundColor: AMBER,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    position: 'relative',
                    zIndex: 1,
                  }}>
                    {/* Navy line on the gold box, so the icon stays visible against it. */}
                    <LineIcon name={step.icon} size={22} color={NAVY} />
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, paddingTop: '4px', position: 'relative', minHeight: '44px' }}>
                    {/* Faded step number */}
                    <div style={{
                      fontFamily: FONT_HEAD,
                      fontSize: '5rem',
                      color: 'rgba(245,166,35,0.08)',
                      lineHeight: 1,
                      position: 'absolute',
                      top: '-14px',
                      right: 0,
                      userSelect: 'none',
                      pointerEvents: 'none',
                    }}>
                      {step.num}
                    </div>
                    <h3 style={{
                      fontWeight: 700,
                      fontSize: '1.05rem',
                      color: NAVY,
                      marginBottom: '0.5rem',
                      lineHeight: 1.3,
                    }}>
                      {step.title}
                    </h3>
                    <p style={{
                      fontSize: '0.9rem',
                      color: 'rgba(13,27,42,0.58)',
                      lineHeight: 1.72,
                      margin: 0,
                    }}>
                      {step.desc}
                    </p>
                  </div>
                </div>

                {/* OR divider between 03a and 03b */}
                {step.orAfter && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    margin: '1.5rem 0 1.5rem 60px',
                  }}>
                    <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(245,166,35,0.18)' }} />
                    <div style={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      letterSpacing: '0.15em',
                      color: AMBER,
                      backgroundColor: 'rgba(245,166,35,0.1)',
                      border: '1px solid rgba(245,166,35,0.28)',
                      borderRadius: '999px',
                      padding: '3px 12px',
                    }}>
                      {t('employer.landing.orDivider')}
                    </div>
                    <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(245,166,35,0.18)' }} />
                  </div>
                )}
              </Reveal>
            ))}
          </div>

          {/* Bottom CTA */}
          <div style={{ marginTop: '4.5rem' }}>
            <button
              onClick={scrollToDemo}
              style={{
                backgroundColor: AMBER,
                color: NAVY,
                border: 'none',
                borderRadius: '8px',
                padding: '15px 32px',
                fontWeight: 700,
                fontSize: '1rem',
                cursor: 'pointer',
                fontFamily: FONT_BODY,
                letterSpacing: '0.01em',
              }}
            >
              {t('employer.landing.howItWorksCta')}
            </button>
          </div>
        </div>
      </section>

      {/* ── 4. COMPARISON TABLE ── */}
      <section id="comparison" style={{ backgroundColor: NAVY, padding: '7rem 2rem' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

          <Reveal>
            {/* Eyebrow */}
            <div style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: AMBER,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              marginBottom: '0.9rem',
            }}>
              {t('employer.landing.compareEyebrow')}
            </div>

            <h2 style={{
              fontFamily: FONT_HEAD,
              fontSize: 'clamp(2.5rem, 5.5vw, 3.75rem)',
              color: WHITE,
              letterSpacing: '0.03em',
              marginBottom: '1.1rem',
              maxWidth: '740px',
              lineHeight: 1.1,
            }}>
              {t('employer.landing.compareHeading')}
            </h2>

            <div style={{ width: '56px', height: '3px', backgroundColor: AMBER, marginBottom: '1.25rem' }} />

            <p style={{
              fontSize: '1.05rem',
              color: 'rgba(255,255,255,0.6)',
              marginBottom: '3.5rem',
              lineHeight: 1.65,
              maxWidth: '580px',
            }}>
              {t('employer.landing.compareIntro')}
            </p>
          </Reveal>

          {/* Table */}
          <Reveal style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="emp-comp-table" style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.9rem',
              tableLayout: 'fixed',
            }}>
              <colgroup>
                <col style={{ width: '26%' }} />
                <col style={{ width: '18.5%' }} />
                <col style={{ width: '18.5%' }} />
                <col style={{ width: '18.5%' }} />
                <col style={{ width: '18.5%' }} />
              </colgroup>

              {/* Header */}
              <thead>
                <tr>
                  {/* Feature label cell */}
                  <th style={{
                    padding: '1rem 1.25rem',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.4)',
                    fontSize: '0.75rem',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    backgroundColor: NAVY,
                  }}>
                    {t('employer.landing.featureColumnHeader')}
                  </th>

                  {/* Vocation — highlighted */}
                  <th style={{
                    padding: '1.25rem 1rem 1rem',
                    textAlign: 'center',
                    borderTop: `3px solid ${AMBER}`,
                    borderBottom: '1px solid rgba(245,166,35,0.2)',
                    backgroundColor: 'rgba(245,166,35,0.05)',
                  }}>
                    <div style={{
                      fontFamily: FONT_HEAD,
                      fontSize: '1.4rem',
                      color: AMBER,
                      letterSpacing: '0.06em',
                      lineHeight: 1,
                      marginBottom: '4px',
                    }}>
                      VOCATION
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: 'rgba(255,255,255,0.55)',
                      fontWeight: 500,
                      marginBottom: '6px',
                    }}>
                      {t('employer.landing.startingAt')}
                    </div>
                    <div style={{
                      display: 'inline-block',
                      backgroundColor: 'rgba(29,158,117,0.18)',
                      border: '1px solid rgba(29,158,117,0.4)',
                      borderRadius: '999px',
                      padding: '2px 10px',
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      color: '#1D9E75',
                      letterSpacing: '0.04em',
                    }}>
                      {t('employer.landing.freeTrial', { days: trialDays })}
                    </div>
                  </th>

                  {['Indeed', 'LinkedIn', 'Recruiter'].map(col => (
                    <th key={col} className="emp-comp-competitor" style={{
                      padding: '1rem',
                      textAlign: 'center',
                      fontFamily: FONT_HEAD,
                      fontSize: '1.2rem',
                      color: 'rgba(255,255,255,0.45)',
                      letterSpacing: '0.04em',
                      borderBottom: '1px solid rgba(255,255,255,0.08)',
                      backgroundColor: NAVY,
                    }}>
                      {col.toUpperCase()}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {[
                  {
                    feature: t('employer.landing.feature1'),
                    vocation: { type: 'yes' },
                    indeed:   { type: 'no' },
                    linkedin: { type: 'no' },
                    recruiter:{ type: 'partial', label: t('employer.landing.feature1RecruiterLabel') },
                  },
                  {
                    feature: t('employer.landing.feature2'),
                    vocation: { type: 'yes' },
                    indeed:   { type: 'no' },
                    linkedin: { type: 'no' },
                    recruiter:{ type: 'no' },
                  },
                  {
                    feature: t('employer.landing.feature6'),
                    vocation: { type: 'yes' },
                    indeed:   { type: 'no' },
                    linkedin: { type: 'no' },
                    recruiter:{ type: 'no' },
                  },
                  {
                    feature: t('employer.landing.feature8'),
                    vocation: { type: 'yes' },
                    indeed:   { type: 'yes' },
                    linkedin: { type: 'yes' },
                    recruiter:{ type: 'no' },
                  },
                  {
                    feature: t('employer.landing.feature9'),
                    vocation: { type: 'text', label: t('employer.landing.feature9VocationLabel') },
                    indeed:   { type: 'text', label: t('employer.landing.feature9IndeedLabel') },
                    linkedin: { type: 'text', label: t('employer.landing.feature9LinkedinLabel'), note: t('employer.landing.feature9LinkedinNote') },
                    recruiter:{ type: 'text', label: t('employer.landing.feature9RecruiterLabel') },
                  },
                ].map((row, i) => {
                  const isAlt = i % 2 === 1;
                  const rowBg = isAlt ? NAVY_MID : NAVY;
                  const vocationBg = isAlt
                    ? 'rgba(245,166,35,0.08)'
                    : 'rgba(245,166,35,0.05)';

                  const renderCell = (cell) => {
                    // Same marks and colors as before, as line icons with a text label for screen readers.
                    if (cell.type === 'yes') return (
                      <LineIcon name="check" size={22} strokeWidth={2.4} color="#1D9E75" label={t('employer.landing.cellYes')} style={{ margin: '0 auto' }} />
                    );
                    if (cell.type === 'no') return (
                      <LineIcon name="x" size={20} strokeWidth={2.4} color="#FF4444" label={t('employer.landing.cellNo')} style={{ margin: '0 auto' }} />
                    );
                    if (cell.type === 'partial') return (
                      <span style={{ color: AMBER, fontSize: '0.8rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        <LineIcon name="warning" size={16} strokeWidth={2} color={AMBER} label={t('employer.landing.cellLimited')} /> {cell.label}
                      </span>
                    );
                    if (cell.type === 'text') return (
                      <div>
                        <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem', fontWeight: 600 }}>
                          {cell.label}
                        </span>
                        {cell.note && (
                          <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', fontWeight: 400, marginTop: '3px' }}>
                            {cell.note}
                          </div>
                        )}
                      </div>
                    );
                    return null;
                  };

                  return (
                    <tr key={i}>
                      <td style={{
                        padding: '1rem 1.25rem',
                        color: 'rgba(255,255,255,0.78)',
                        fontWeight: 500,
                        backgroundColor: rowBg,
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        lineHeight: 1.4,
                      }}>
                        {row.feature}
                      </td>
                      <td style={{
                        padding: '1rem',
                        textAlign: 'center',
                        backgroundColor: vocationBg,
                        borderBottom: '1px solid rgba(245,166,35,0.12)',
                        borderLeft: '1px solid rgba(245,166,35,0.12)',
                        borderRight: '1px solid rgba(245,166,35,0.12)',
                      }}>
                        {renderCell(row.vocation)}
                      </td>
                      {[row.indeed, row.linkedin, row.recruiter].map((cell, j) => (
                        <td key={j} className="emp-comp-competitor" style={{
                          padding: '1rem',
                          textAlign: 'center',
                          backgroundColor: rowBg,
                          borderBottom: '1px solid rgba(255,255,255,0.05)',
                        }}>
                          {renderCell(cell)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Reveal>
          <p style={{ maxWidth: '960px', margin: '0.85rem auto 0', fontSize: '14px', lineHeight: 1.5, color: 'rgba(255,255,255,0.45)', textAlign: 'center' }}>
            {t('employer.landing.compareFootnote')}
          </p>

          {/* What's included at $199/mo */}
          <Reveal style={{ maxWidth: '480px', margin: '2.5rem auto 0', textAlign: 'center' }}>
            <div style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: AMBER,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              marginBottom: '1rem',
            }}>
              {t('employer.landing.includedHeading')}
            </div>
            <div style={{
              backgroundColor: WHITE,
              borderRadius: '10px',
              padding: '28px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
              textAlign: 'left',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {[
                  t('employer.landing.included1'),
                  t('employer.landing.included2'),
                  t('employer.landing.included3'),
                  t('employer.landing.included4'),
                  t('employer.landing.included5'),
                ].map(item => (
                  <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.92rem', color: NAVY }}>
                    <LineIcon name="check" size={16} strokeWidth={2.6} color="#16A34A" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '1.1rem' }}>
              <span
                onClick={() => navigate('/employer/pricing')}
                style={{ color: AMBER, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}
              >
                {t('employer.landing.seeFullPricingLink')} →
              </span>
            </div>
          </Reveal>

          {/* Callout box */}
          <Reveal style={{
            marginTop: '3rem',
            backgroundColor: NAVY_MID,
            border: `1px solid rgba(245,166,35,0.4)`,
            borderRadius: '14px',
            padding: '2.25rem 2.5rem',
            textAlign: 'center',
            maxWidth: '780px',
            margin: '3rem auto 0',
          }}>
            <p style={{
              fontSize: '1.05rem',
              color: 'rgba(255,255,255,0.8)',
              lineHeight: 1.75,
              marginBottom: '1.75rem',
            }}>
              <span style={{ color: AMBER, fontWeight: 700 }}>
                {t('employer.landing.calloutHighlight')}
              </span>{' '}
              {t('employer.landing.calloutRest')}
            </p>
            <button
              onClick={scrollToDemo}
              style={{
                backgroundColor: AMBER,
                color: NAVY,
                border: 'none',
                borderRadius: '8px',
                padding: '14px 30px',
                fontWeight: 700,
                fontSize: '1rem',
                cursor: 'pointer',
                fontFamily: FONT_BODY,
                letterSpacing: '0.01em',
              }}
            >
              {t('employer.landing.bookFreeDemo')}
            </button>
          </Reveal>

        </div>
      </section>

      {/* ── 5. WHY VOCATION FOR EMPLOYERS ── */}
      <section id="why-vocation" style={{ backgroundColor: WHITE, padding: '7rem 2rem' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <Reveal>
            {/* Eyebrow */}
            <div style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: AMBER,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              marginBottom: '0.9rem',
            }}>
              {t('employer.landing.whyEyebrow')}
            </div>

            <h2 style={{
              fontFamily: FONT_HEAD,
              fontSize: 'clamp(2.5rem, 5.5vw, 3.75rem)',
              color: NAVY,
              letterSpacing: '0.03em',
              marginBottom: '1.1rem',
              maxWidth: '680px',
              lineHeight: 1.1,
            }}>
              {t('employer.landing.whyHeading')}
            </h2>

            <div style={{ width: '56px', height: '3px', backgroundColor: AMBER, marginBottom: '4rem' }} />
          </Reveal>

          <div className="emp-why-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.25rem' }}>
            {[
              {
                icon: 'target',
                title: t('employer.landing.why1Title'),
                desc: t('employer.landing.why1Desc'),
              },
              {
                icon: 'checkCircle',
                title: t('employer.landing.why2Title'),
                desc: t('employer.landing.why2Desc'),
              },
              {
                icon: 'message',
                title: t('employer.landing.why3Title'),
                desc: t('employer.landing.why3Desc'),
              },
              {
                icon: 'dollar',
                title: t('employer.landing.why4Title'),
                desc: t('employer.landing.why4Desc'),
              },
              {
                icon: 'clipboard',
                title: t('employer.landing.why5Title'),
                desc: t('employer.landing.why5Desc'),
              },
              {
                icon: 'city',
                title: t('employer.landing.why6Title'),
                desc: t('employer.landing.why6Desc'),
              },
            ].map((f, i) => (
              <Reveal stagger key={i} style={{
                backgroundColor: WHITE,
                borderTop: `3px solid ${AMBER}`,
                boxShadow: '0 2px 16px rgba(13,27,42,0.08)',
                borderRadius: '12px',
                padding: '1.75rem',
                display: 'flex',
                gap: '1.25rem',
                alignItems: 'flex-start',
              }}>
                <div style={{
                  backgroundColor: AMBER,
                  borderRadius: '8px',
                  width: '46px',
                  height: '46px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <LineIcon name={f.icon} size={24} color={NAVY} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: NAVY, marginBottom: '0.45rem', lineHeight: 1.3 }}>
                    {f.title}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#666', lineHeight: 1.65 }}>
                    {f.desc}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. BOOK A DEMO ── */}
      <section
        ref={demoRef}
        id="book-demo"
        style={{
          background: `linear-gradient(160deg, ${NAVY_MID} 0%, ${NAVY} 100%)`,
          padding: '7rem 2rem',
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center',
        }}
      >
        {/* Glow */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          height: '360px',
          background: 'radial-gradient(ellipse, rgba(245,166,35,0.13) 0%, transparent 68%)',
          pointerEvents: 'none',
        }} />

        <div style={{ position: 'relative', maxWidth: '760px', margin: '0 auto' }}>
          <Reveal as="h2" style={{
            fontFamily: FONT_HEAD,
            fontSize: 'clamp(2.5rem, 6vw, 4rem)',
            color: WHITE,
            letterSpacing: '0.03em',
            marginBottom: '0.9rem',
          }}>
            {t('employer.landing.demoHeading')}
          </Reveal>
          <Reveal as="p" style={{
            fontSize: '1.05rem',
            color: 'rgba(255,255,255,0.62)',
            marginBottom: '2.5rem',
            lineHeight: 1.65,
          }}>
            {t('employer.landing.demoSubtitle')}
          </Reveal>

          {/* Calendly embed */}
          <div style={{ marginBottom: '2.5rem', textAlign: 'left' }}>
            <h3 style={{
              fontFamily: FONT_HEAD,
              fontSize: 'clamp(1.5rem, 3.5vw, 2rem)',
              color: AMBER,
              letterSpacing: '0.04em',
              marginBottom: '0.4rem',
            }}>
              {t('employer.landing.calendlyHeading')}
            </h3>
            <p style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.45)', marginBottom: '1.25rem' }}>
              {t('employer.landing.calendlySubtext')}
            </p>
            <div
              className="calendly-inline-widget"
              data-url="https://calendly.com/alexis-vocationworks/30min?utm_source=landing&utm_medium=embed&utm_campaign=demo&utm_content=embed"
              style={{ minWidth: '320px', height: '700px' }}
            />
          </div>

          {/* Divider */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '1rem',
            marginBottom: '2rem',
          }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.12)' }} />
            <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.38)', whiteSpace: 'nowrap' }}>
              {t('employer.landing.orFillOutForm')}
            </span>
            <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.12)' }} />
          </div>

          {submitted ? (
            <div style={{
              color: AMBER,
              fontWeight: 700,
              fontSize: '1.15rem',
              padding: '1.75rem',
              border: `1px solid rgba(245,166,35,0.35)`,
              borderRadius: '10px',
              backgroundColor: 'rgba(245,166,35,0.08)',
              lineHeight: 1.6,
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <LineIcon name="checkCircle" color="#1D9E75" />
                {t('employer.landing.successHeading')}
              </span><br />
              <span style={{ fontWeight: 400, fontSize: '0.95rem', color: 'rgba(255,255,255,0.6)' }}>
                {t('employer.landing.successSubtext')}
              </span>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              style={{ display: 'flex', flexDirection: 'column', gap: '1rem', textAlign: 'left' }}
            >
              <input
                className="emp-input"
                type="text"
                name="name"
                required
                value={form.name}
                onChange={handleChange}
                placeholder={t('employer.landing.formNamePlaceholder')}
                style={inputStyle}
              />
              <input
                className="emp-input"
                type="text"
                name="company"
                required
                value={form.company}
                onChange={handleChange}
                placeholder={t('employer.landing.formCompanyPlaceholder')}
                style={inputStyle}
              />
              <input
                className="emp-input"
                type="email"
                name="email"
                required
                value={form.email}
                onChange={handleChange}
                placeholder={t('employer.landing.formEmailPlaceholder')}
                style={inputStyle}
              />
              <input
                className="emp-input"
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder={t('employer.landing.formPhonePlaceholder')}
                style={inputStyle}
              />
              <button
                type="submit"
                style={{
                  backgroundColor: AMBER,
                  color: NAVY,
                  border: 'none',
                  borderRadius: '8px',
                  padding: '15px',
                  fontWeight: 700,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  fontFamily: FONT_BODY,
                  marginTop: '0.25rem',
                  letterSpacing: '0.01em',
                }}
              >
                {t('employer.landing.formSubmit')}
              </button>
            </form>
          )}

          <p style={{
            fontSize: '0.75rem',
            color: 'rgba(255,255,255,0.38)',
            marginTop: '1rem',
            lineHeight: 1.6,
          }}>
            {t('employer.landing.formDisclaimer')}
          </p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <SiteFooter />

    </div>
  );
}
